import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export function ThemeProvider({ children, authFetch, user }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'system');

  // On first load, fetch admin default if no user preference stored
  useEffect(() => {
    if (!localStorage.getItem('theme') && !user?.theme) {
      fetch('/api/admin/site-settings')
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.default_theme && !localStorage.getItem('theme')) {
            setTheme(data.default_theme);
          }
        })
        .catch(() => {});
    }
  }, []);

  // Sync user DB preference on login
  useEffect(() => {
    if (user?.theme && !localStorage.getItem('theme')) {
      setTheme(user.theme);
    }
  }, [user?.theme]);

  // Apply data-theme attribute immediately when theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else if (theme === 'light') {
      root.removeAttribute('data-theme');
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) root.setAttribute('data-theme', 'dark');
      else root.removeAttribute('data-theme');
    }
  }, [theme]);

  // Listen for system changes when theme === 'system'
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      document.documentElement[e.matches ? 'setAttribute' : 'removeAttribute']('data-theme', 'dark');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setAndPersistTheme = async (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    if (authFetch) {
      try {
        await authFetch('/api/users/profile/theme', {
          method: 'PATCH',
          body: JSON.stringify({ theme: newTheme })
        });
      } catch (e) { /* non-fatal */ }
    }
  };

  const isDark = theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setAndPersistTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
